import React, { useState, useEffect, useCallback } from "react";
import {
  ClipboardList, Users, CheckCircle, XCircle, Clock,
  Calendar, Download, RefreshCw, Loader, ChevronDown,
  BarChart2, BookOpen, Search,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  lmsGetStudents, lmsGetClasses, lmsGetManualAttendance,
  lmsMarkManualAttendance, lmsGetAttendanceReport,
  lmsGetSchoolClasses, lmsGetBatches,
} from "../api/lmsApi";
import "../lms.css";

const getUserData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_CONFIG = {
  present: { label: "Present", color: "#16a34a", bg: "#dcfce7", border: "#86efac", icon: CheckCircle },
  absent:  { label: "Absent",  color: "#dc2626", bg: "#fee2e2", border: "#fca5a5", icon: XCircle },
  late:    { label: "Late",    color: "#d97706", bg: "#fef3c7", border: "#fcd34d", icon: Clock },
};

/* ═══════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════ */
export default function AttendanceManagement() {
  const [tab, setTab] = useState("mark");

  return (
    <div className="lms-content-inner">
      {/* Header card — contains title, tabs, and filters all together */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />
        <div style={{ padding: "16px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12,
            background: "linear-gradient(135deg,#4f46e5,#a855f7)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <ClipboardList size={22} color="#fff" />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>Attendance Management</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Mark daily attendance and view reports</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderTop: "1px solid #f1f5f9", padding: "0 22px" }}>
          {[
            { key: "mark",   label: "Mark Attendance", icon: ClipboardList },
            { key: "report", label: "Daily Report",    icon: BarChart2 },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: "12px 20px", border: "none", background: "none",
              fontWeight: tab === key ? 700 : 500,
              color: tab === key ? "#4f46e5" : "#6b7280",
              borderBottom: tab === key ? "2.5px solid #4f46e5" : "2.5px solid transparent",
              cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6,
              marginBottom: -1,
            }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

      </div>

      {tab === "mark"   && <MarkAttendanceTab />}
      {tab === "report" && <ReportTab />}
    </div>
  );
}

/* ── Shared filter input styles ── */
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  border: "1.5px solid #e5e7eb", borderRadius: 8,
  padding: "9px 12px", fontSize: 13, color: "#374151",
  background: "#fff", outline: "none", appearance: "none",
  fontFamily: "inherit",
};
const searchWrapStyle = {
  display: "flex", alignItems: "center", gap: 8,
  border: "1.5px solid #e5e7eb", borderRadius: 8,
  padding: "8px 12px", background: "#fff",
};
const searchInputStyle = {
  border: "none", background: "transparent", outline: "none",
  fontSize: 13, color: "#374151", flex: 1, minWidth: 0,
  fontFamily: "inherit",
};

/* ═══════════════════════════════════════════
   TAB 1 — MARK ATTENDANCE
═══════════════════════════════════════════ */
function MarkAttendanceTab() {
  const ud = getUserData();
  const schoolId  = ud.school_id || ud.admin_id || null;
  const teacherId = ud.teacher_id || ud.admin_id || null;

  const [classes,    setClasses]    = useState([]);
  const [batches,    setBatches]    = useState([]);
  const [students,   setStudents]   = useState([]);
  const [existing,   setExisting]   = useState({});
  const [attendance, setAttendance] = useState({});
  const [classId,    setClassId]    = useState("");
  const [batchId,    setBatchId]    = useState("");
  const [date,       setDate]       = useState(today());
  const [search,     setSearch]     = useState("");
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    lmsGetSchoolClasses(schoolId)
      .then((res) => setClasses(res.data?.data || res.data || []))
      .catch(() => {});
  }, [schoolId]);

  // Load batches when class changes
  useEffect(() => {
    if (!classId) { setBatches([]); setBatchId(""); return; }
    lmsGetBatches({ school_id: schoolId, class_id: classId })
      .then((res) => setBatches(res.data?.data || []))
      .catch(() => setBatches([]));
  }, [classId, schoolId]);

  useEffect(() => {
    if (!classId) { setStudents([]); return; }
    lmsGetStudents({ class_id: classId, school_id: schoolId })
      .then((res) => {
        const list = res.data?.data || res.data || [];
        setStudents(list);
        const def = {};
        list.forEach((s) => { def[s.id] = "absent"; });
        setAttendance(def);
      })
      .catch(() => {});
  }, [classId, schoolId]);

  useEffect(() => {
    if (!classId || !date) return;
    const params = { class_id: classId, date, school_id: schoolId };
    if (batchId) params.batch_id = batchId;
    lmsGetManualAttendance(params)
      .then((res) => {
        const rows = res.data?.data || [];
        const map = {};
        rows.forEach((r) => { map[r.student_id] = r.status; });
        setExisting(map);
        setAttendance((prev) => ({ ...prev, ...map }));
      })
      .catch(() => {});
  }, [classId, batchId, date, schoolId]);

  const setAll = (status) => {
    const updated = {};
    students.forEach((s) => { updated[s.id] = status; });
    setAttendance(updated);
  };

  const toggle = (studentId, status) => {
    setAttendance((prev) => ({ ...prev, [studentId]: status }));
  };

  const handleSave = async () => {
    if (!classId) return toast.error("Select a class first");
    if (!students.length) return toast.error("No students in this class");
    setSaving(true);
    try {
      const records = students.map((s) => ({
        school_id:    schoolId,
        class_id:     classId,
        batch_id:     batchId || null,
        student_id:   s.id,
        student_name: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
        date,
        status:       attendance[s.id] || "absent",
        marked_by:    teacherId,
      }));
      await lmsMarkManualAttendance(records);
      setExisting(attendance);
      toast.success("Attendance saved successfully!");
    } catch {
      toast.error("Failed to save attendance");
    } finally { setSaving(false); }
  };

  const handleExport = () => {
    const rows = students.map((s) => ({
      Name: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
      "Admission No": s.admission_number || "",
      Date: date,
      Status: attendance[s.id] || "absent",
    }));
    const header = Object.keys(rows[0] || {});
    const csv = [header, ...rows.map((r) => header.map((h) => `"${r[h]}"`))]
      .map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `attendance-${classId}-${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = students.filter((s) =>
    !search || `${s.first_name} ${s.last_name}`.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    present: Object.values(attendance).filter((v) => v === "present").length,
    absent:  Object.values(attendance).filter((v) => v === "absent").length,
    late:    Object.values(attendance).filter((v) => v === "late").length,
  };

  return (
    <div>
      {/* Filter row */}
      <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto auto", gap: 10, alignItems: "end" }}>
        <div className="crs-field" style={{ margin: 0 }}>
          <select value={classId} onChange={(e) => { setClassId(e.target.value); setBatchId(""); }}>
            <option value="">Select Class</option>
            {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.class_name}</option>)}
          </select>
        </div>

        <div className="crs-field" style={{ margin: 0 }}>
          <select value={batchId} onChange={(e) => setBatchId(e.target.value)} disabled={!classId || !batches.length}>
            <option value="">All Batches</option>
            {batches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.section ? ` (${b.section})` : ""}</option>)}
          </select>
        </div>

        <div className="crs-field" style={{ margin: 0 }}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className="crs-field" style={{ margin: 0 }}>
          <input placeholder="Search student..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <button onClick={() => setAll("present")} style={{
          padding: "9px 16px", borderRadius: 8, border: "1.5px solid #86efac",
          background: "#dcfce7", color: "#16a34a", fontSize: 12, fontWeight: 700,
          cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5,
        }}>
          <CheckCircle size={13} /> All Present
        </button>

        <button onClick={() => setAll("absent")} style={{
          padding: "9px 16px", borderRadius: 8, border: "1.5px solid #fca5a5",
          background: "#fee2e2", color: "#dc2626", fontSize: 12, fontWeight: 700,
          cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5,
        }}>
          <XCircle size={13} /> All Absent
        </button>
      </div>

      {/* Stats bar */}
      {students.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
          {[
            { label: "Total",   value: students.length, color: "#4f46e5", bg: "#eef2ff", border: "#c7d2fe" },
            { label: "Present", value: counts.present,  color: "#16a34a", bg: "#dcfce7", border: "#86efac" },
            { label: "Absent",  value: counts.absent,   color: "#dc2626", bg: "#fee2e2", border: "#fca5a5" },
            { label: "Late",    value: counts.late,     color: "#d97706", bg: "#fef3c7", border: "#fcd34d" },
          ].map(({ label, value, color, bg, border }) => (
            <div key={label} style={{
              background: "#fff", borderRadius: 12, padding: "14px 18px",
              border: `1.5px solid ${border}`, overflow: "hidden",
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: bg,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color }}>{value}</span>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, marginTop: 2, letterSpacing: "0.04em" }}>
                  {label.toUpperCase()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Student cards grid */}
      {!classId ? (
        <div style={{ textAlign: "center", padding: "70px 0", background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <BookOpen size={28} color="#d1d5db" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Select a class to mark attendance</div>
          <div style={{ fontSize: 13, color: "#9ca3af" }}>Choose a class from the dropdown above</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "70px 0", background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <Users size={28} color="#d1d5db" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>No students found</div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          {/* Table header */}
          <div style={{
            display: "grid", gridTemplateColumns: "48px 1fr 140px 240px",
            padding: "10px 20px", background: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
            fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: "0.05em",
          }}>
            <span>#</span>
            <span>STUDENT</span>
            <span>ADMISSION NO</span>
            <span>STATUS</span>
          </div>

          {filtered.map((s, i) => {
            const status    = attendance[s.id] || "absent";
            const cfg       = STATUS_CONFIG[status];
            const wasMarked = !!existing[s.id];
            const initial   = (s.first_name || "?")[0].toUpperCase();

            return (
              <div key={s.id} style={{
                display: "grid", gridTemplateColumns: "48px 1fr 140px 240px",
                padding: "12px 20px", alignItems: "center",
                borderBottom: "1px solid #f1f5f9",
                background: i % 2 === 0 ? "#fff" : "#fafafa",
              }}>
                {/* # */}
                <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>{i + 1}</span>

                {/* Name */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                    background: cfg.bg, border: `2px solid ${cfg.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 800, color: cfg.color,
                  }}>
                    {initial}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                      {s.first_name} {s.last_name}
                    </div>
                    {wasMarked && (
                      <div style={{ fontSize: 10, color: "#6d28d9", fontWeight: 600 }}>&#10003; Marked</div>
                    )}
                  </div>
                </div>

                {/* Admission No */}
                <span style={{ fontSize: 12, color: "#6b7280" }}>{s.admission_number || "—"}</span>

                {/* Status toggle */}
                <div style={{ display: "flex", gap: 6 }}>
                  {Object.entries(STATUS_CONFIG).map(([key, c]) => {
                    const Icon   = c.icon;
                    const active = status === key;
                    return (
                      <button key={key} onClick={() => toggle(s.id, key)} style={{
                        flex: 1, padding: "6px 4px", borderRadius: 8,
                        border: `1.5px solid ${active ? c.color : "#e5e7eb"}`,
                        background: active ? c.bg : "#fff",
                        color: active ? c.color : "#9ca3af",
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                        transition: "all 0.15s",
                      }}>
                        <Icon size={11} /> {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Save + Export */}
      {students.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button onClick={handleExport} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "10px 18px",
            borderRadius: 9, border: "1.5px solid #e5e7eb",
            background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
            <Download size={14} /> Export CSV
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "10px 24px",
            borderRadius: 9, border: "none",
            background: saving ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            {saving ? <Loader size={14} className="ms2-spin" /> : <CheckCircle size={14} />}
            {saving ? "Saving..." : "Save Attendance"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   TAB 2 — DAILY REPORT
═══════════════════════════════════════════ */
function ReportTab() {
  const ud = getUserData();
  const schoolId = ud.school_id || ud.admin_id || null;

  const [classes,  setClasses]  = useState([]);
  const [classId,  setClassId]  = useState("");
  const [date,     setDate]     = useState(today());
  const [report,   setReport]   = useState([]);
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    lmsGetClasses()
      .then((res) => setClasses(res.data?.data || res.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [repRes, recRes] = await Promise.all([
        lmsGetAttendanceReport({ school_id: schoolId, class_id: classId || undefined, date }),
        lmsGetManualAttendance({ school_id: schoolId, class_id: classId || undefined, date }),
      ]);
      setReport(repRes.data?.data || []);
      setRecords(recRes.data?.data || []);
    } catch {
      toast.error("Failed to load report");
    } finally { setLoading(false); }
  }, [schoolId, classId, date]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    const header = ["Student Name", "Date", "Status", "Class"];
    const rows = records.map((r) => [r.student_name, r.date, r.status, r.class_id]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `report-${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Filter row — 3 columns + buttons */}
      <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto auto", gap: 10, alignItems: "end" }}>
        <div className="crs-field" style={{ margin: 0 }}>
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">All Classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="crs-field" style={{ margin: 0 }}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        {/* spacer */}
        <span />

          <button onClick={load} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            borderRadius: 9, border: "1.5px solid #e5e7eb",
            background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer",
            whiteSpace: "nowrap",
          }}>
            <RefreshCw size={13} /> Refresh
          </button>

          <button onClick={handleExport} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 16px",
            borderRadius: 9, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            whiteSpace: "nowrap",
          }}>
            <Download size={13} /> Export CSV
          </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader size={28} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : (
        <>
          {/* Summary cards per class */}
          {report.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
              {report.map((r, i) => {
                const className = classes.find((c) => String(c.id) === String(r.class_id))?.name || `Class ${r.class_id}`;
                const pct = r.attendance_pct || 0;
                const good = pct >= 75;
                return (
                  <div key={i} style={{
                    background: "#fff", borderRadius: 12, overflow: "hidden",
                    border: "1.5px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}>
                    <div style={{ height: 4, background: good ? "#16a34a" : "#dc2626" }} />
                    <div style={{ padding: "16px 18px" }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 12 }}>
                        {className}
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", marginBottom: 5 }}>
                          <span>Attendance Rate</span>
                          <span style={{ fontWeight: 800, color: good ? "#16a34a" : "#dc2626" }}>{pct}%</span>
                        </div>
                        <div style={{ height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{
                            height: "100%", borderRadius: 4, width: `${pct}%`,
                            background: good ? "#16a34a" : "#dc2626", transition: "width 0.4s",
                          }} />
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
                        {[
                          { label: "Total",   value: r.total,   color: "#4f46e5" },
                          { label: "Present", value: r.present, color: "#16a34a" },
                          { label: "Absent",  value: r.absent,  color: "#dc2626" },
                          { label: "Late",    value: r.late,    color: "#d97706" },
                        ].map(({ label, value, color }) => (
                          <div key={label} style={{ textAlign: "center", padding: "8px 4px", borderRadius: 8, background: "#f9fafb" }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value || 0}</div>
                            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Student-level records table */}
          {records.length === 0 ? (
            <div style={{ textAlign: "center", padding: "70px 0", background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: "#f3f4f6",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <Calendar size={28} color="#d1d5db" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                No attendance records for {date}
              </div>
              <div style={{ fontSize: 13, color: "#9ca3af" }}>
                Switch to "Mark Attendance" tab to add records
              </div>
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 120px 120px",
                padding: "10px 20px", background: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: "0.04em",
              }}>
                <span>STUDENT</span>
                <span>DATE</span>
                <span>CLASS</span>
                <span>STATUS</span>
              </div>
              {records.map((r, i) => {
                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.absent;
                const Icon = cfg.icon;
                const className = classes.find((c) => String(c.id) === String(r.class_id))?.name || `Class ${r.class_id}`;
                return (
                  <div key={r.id || i} style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr 120px 120px",
                    padding: "11px 20px", alignItems: "center",
                    borderBottom: "1px solid #f1f5f9",
                    background: i % 2 === 0 ? "#fff" : "#fafafa",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: "50%",
                        background: cfg.bg, border: `1.5px solid ${cfg.border}`,
                        color: cfg.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {(r.student_name || "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{r.student_name}</span>
                    </div>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{r.date}</span>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{className}</span>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 8,
                      background: cfg.bg, color: cfg.color, width: "fit-content",
                      border: `1px solid ${cfg.border}`,
                    }}>
                      <Icon size={11} /> {cfg.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
